import { useState, useMemo } from "react";
import type { RangeValue } from "@/shared/ui/DatePicker/useCalendar";
import { useGetTrialBalanceQuery, useGetJournalEntriesQuery } from "../api";
import { formatDateLocal } from "../utils";

const CURRENT_YEAR = new Date().getFullYear();

function createDefaultRange(): RangeValue {
  return {
    start: new Date(CURRENT_YEAR, 0, 1),
    end: new Date(CURRENT_YEAR, 11, 31),
  };
}

interface UseReportsPageParams {
  projectId?: number | null;
}

export function useReportsPage(params?: UseReportsPageParams) {
  const projectId = params?.projectId ?? undefined;
  const [dateRange, setDateRange] = useState<RangeValue>(createDefaultRange);

  const isRangeComplete = dateRange.start !== null && dateRange.end !== null;

  const appliedPeriodDates = useMemo(function computePeriodDates() {
    if (!dateRange.start || !dateRange.end) {
      return { start_date: "", end_date: "" };
    }
    return {
      start_date: formatDateLocal(dateRange.start),
      end_date: formatDateLocal(dateRange.end),
    };
  }, [dateRange]);

  const { start_date, end_date } = appliedPeriodDates;
  const projectFilter = projectId ? { project_id: projectId } : {};
  const skipQueries = !isRangeComplete;

  const trialBalanceQuery = useGetTrialBalanceQuery(
    { start_date, end_date, ...projectFilter },
    { skip: skipQueries },
  );

  const journalEntriesQuery = useGetJournalEntriesQuery(
    { start_date, end_date, ...projectFilter },
    { skip: skipQueries },
  );

  const isFetchingAny = trialBalanceQuery.isFetching || journalEntriesQuery.isFetching;

  return {
    dateRange,
    setDateRange,
    appliedPeriodDates,
    trialBalance: trialBalanceQuery.data,
    journalEntries: journalEntriesQuery.data,
    isLoadingTrialBalance: trialBalanceQuery.isLoading,
    isLoadingJournalEntries: journalEntriesQuery.isLoading,
    isFetchingAny,
    trialBalanceError: trialBalanceQuery.isError ? trialBalanceQuery.error : undefined,
    journalEntriesError: journalEntriesQuery.isError ? journalEntriesQuery.error : undefined,
    isLoading: trialBalanceQuery.isLoading || journalEntriesQuery.isLoading,
    isError: trialBalanceQuery.isError || journalEntriesQuery.isError,
  };
}
